import React, { useEffect, useState, useCallback } from 'react';
import './TopicDocumentsPanel.css';

/**
 * Modal listing documents most relevant to a (topic, industry) cut.
 *
 * Props:
 *   topic, industry — slice identifiers (industry optional)
 *   topicLabel      — pretty label to show in the header (defaults to topic)
 *   industryLabel   — pretty label for the industry chip
 *   fetcher         — () => Promise<{documents, totalDocuments}> (already
 *                     wired to auth + url; injected by the parent so this
 *                     component stays auth-agnostic)
 *   onOpenDocument  — (filename, paraIdx) => void
 *   onClose         — () => void
 */
function TopicDocumentsPanel({
  topic,
  industry,
  topicLabel,
  industryLabel,
  fetcher,
  onOpenDocument,
  onClose,
}) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  // ESC closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Load when topic/industry change.
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    fetcher()
      .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || String(err), data: null }); });
    return () => { cancelled = true; };
  }, [topic, industry, fetcher]);

  const handleBackdrop = useCallback((e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  }, [onClose]);

  const documents = state.data?.documents || [];
  const total = state.data?.totalProjects ?? state.data?.totalDocuments ?? 0;
  const totalDocs = state.data?.totalDocuments ?? 0;

  // Concentration bar width proportional to the top doc's paragraph count.
  const maxParas = documents.reduce((m, d) => Math.max(m, d.paragraphsInTopic || 0), 1);

  return (
    <div className="tdp-backdrop" onClick={handleBackdrop} role="dialog" aria-modal="true">
      <div className="tdp-modal">
        <div className="tdp-header">
          <div className="tdp-header-content">
            <p className="tdp-eyebrow">Projects about</p>
            <h2 className="tdp-title">{topicLabel || topic}</h2>
            <p className="tdp-meta">
              {industry && <>in <strong>{industryLabel || industry}</strong> · </>}
              {total > 0 && (
                <>
                  {total.toLocaleString()} project{total === 1 ? '' : 's'} match this topic
                  {totalDocs > total && <> ({totalDocs.toLocaleString()} outputs)</>}
                   — showing top {documents.length}
                </>
              )}
            </p>
          </div>
          <button type="button" className="tdp-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        {state.loading && <div className="tdp-loading">Loading…</div>}

        {!state.loading && state.error && (
          <div className="tdp-empty">Couldn't load documents: {state.error}</div>
        )}

        {!state.loading && !state.error && documents.length === 0 && (
          <div className="tdp-empty">No documents match this slice.</div>
        )}

        {!state.loading && !state.error && documents.length > 0 && (
          <ol className="tdp-list">
            {documents.map((d, idx) => {
              const widthPct = Math.max(8, Math.round((d.paragraphsInTopic / maxParas) * 100));
              return (
                <li key={d.filename + idx} className="tdp-row">
                  <span className="tdp-rank">{idx + 1}.</span>
                  <div className="tdp-body">
                    <h3 className="tdp-doc-title">
                      {d.acronym && <span className="tdp-acronym">{d.acronym}</span>}
                      {d.title || `Project ${(d.filename || '').split('_')[0]}`}
                    </h3>
                    <p className="tdp-doc-meta">
                      <span>
                        <span className="tdp-bar" style={{ width: `${widthPct}px` }} />
                        {d.paragraphsInTopic} paragraph{d.paragraphsInTopic === 1 ? '' : 's'} on topic
                      </span>
                      {d.deliverableCount > 1 && (
                        <span>across {d.deliverableCount} output{d.deliverableCount === 1 ? '' : 's'}</span>
                      )}
                      {d.docAccess > 0 && <span>{d.docAccess} access{d.docAccess === 1 ? '' : 'es'}</span>}
                    </p>
                    {d.snippet && (
                      <p className="tdp-snippet">
                        "{d.snippet}"
                        {d.deliverableCount > 1 && (
                          <span className="tdp-snippet-src"> — best excerpt from output {d.filename}</span>
                        )}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="tdp-open-btn"
                    onClick={() => onOpenDocument && onOpenDocument(d.filename, d.paraIdx)}
                    title={`Open output ${d.filename} at the best-matching paragraph`}
                  >
                    Open ↗
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

export default TopicDocumentsPanel;
